import { useEffect, useMemo, useRef, useState } from 'react'

import { cn } from '../utilitarios/cn'
import { Caixa } from './caixa'
import { Entrada } from './entrada'
import { classesControle } from './campo'

export interface OpcaoMultipla {
  valor: string
  rotulo: string
  descricao?: string
}

export interface PropriedadesSelecaoMultipla {
  opcoes: ReadonlyArray<OpcaoMultipla>
  selecionados: ReadonlyArray<string>
  onAlterar: (selecionados: string[]) => void
  /** Texto do gatilho quando nada está selecionado ("Nenhuma seleção" da TELA-02). */
  placeholder?: string
  temErro?: boolean
  desabilitado?: boolean
  /** Mostra campo de busca quando há muitas opções (equipes passam de 30). */
  buscavel?: boolean
  id?: string
  className?: string
}

/**
 * Multi-seleção em popover — a "Equipe (2 Itens Selecionados)" da TELA-10 e o
 * filtro "Atendentes" da TELA-02.
 *
 * Não é `<select multiple>`: o controle nativo esconde o que está selecionado
 * quando a lista é maior que a caixa e não tem busca. Aqui o gatilho resume a
 * seleção e a lista é de checkbox, que é o que a operação entende como "marcar
 * vários".
 */
export function SelecaoMultipla({
  opcoes,
  selecionados,
  onAlterar,
  placeholder = 'Nenhuma seleção',
  temErro,
  desabilitado,
  buscavel,
  id,
  className,
}: PropriedadesSelecaoMultipla) {
  const [aberto, setAberto] = useState(false)
  const [busca, setBusca] = useState('')
  const container = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!aberto) return
    function aoClicarFora(evento: MouseEvent) {
      if (!container.current?.contains(evento.target as Node)) setAberto(false)
    }
    function aoTeclar(evento: KeyboardEvent) {
      if (evento.key === 'Escape') setAberto(false)
    }
    document.addEventListener('mousedown', aoClicarFora)
    document.addEventListener('keydown', aoTeclar)
    return () => {
      document.removeEventListener('mousedown', aoClicarFora)
      document.removeEventListener('keydown', aoTeclar)
    }
  }, [aberto])

  const visiveis = useMemo(() => {
    const termo = busca.trim().toLowerCase()
    if (!termo) return opcoes
    return opcoes.filter((opcao) => opcao.rotulo.toLowerCase().includes(termo))
  }, [opcoes, busca])

  const resumo = useMemo(() => {
    if (selecionados.length === 0) return placeholder
    if (selecionados.length === 1) {
      return opcoes.find((opcao) => opcao.valor === selecionados[0])?.rotulo ?? '1 item selecionado'
    }
    return `${selecionados.length} itens selecionados`
  }, [selecionados, opcoes, placeholder])

  function alternar(valor: string) {
    const jaTem = selecionados.includes(valor)
    onAlterar(
      jaTem ? selecionados.filter((item) => item !== valor) : [...selecionados, valor],
    )
  }

  return (
    <div ref={container} className={cn('relative', className)}>
      <button
        type="button"
        id={id}
        disabled={desabilitado}
        aria-expanded={aberto}
        aria-haspopup="listbox"
        onClick={() => setAberto((valor) => !valor)}
        className={cn(
          classesControle(temErro),
          'flex h-10 items-center justify-between gap-2 text-left',
          !desabilitado && 'cursor-pointer',
        )}
      >
        <span className={cn('truncate', selecionados.length === 0 && 'text-conteudo-fraco')}>
          {resumo}
        </span>
        <svg aria-hidden="true" viewBox="0 0 20 20" className="h-4 w-4 shrink-0 text-conteudo-fraco" fill="currentColor">
          <path d="M5.2 7.4a1 1 0 0 1 1.4 0L10 10.8l3.4-3.4a1 1 0 1 1 1.4 1.4l-4.1 4.1a1 1 0 0 1-1.4 0L5.2 8.8a1 1 0 0 1 0-1.4Z" />
        </svg>
      </button>

      {aberto && (
        <div
          role="listbox"
          aria-multiselectable="true"
          className="absolute z-30 mt-1 w-full rounded-md border border-borda bg-superficie p-1 shadow-lg"
        >
          {buscavel && (
            <div className="p-1.5">
              <Entrada
                autoFocus
                value={busca}
                onChange={(evento) => setBusca(evento.target.value)}
                placeholder="Buscar…"
                className="h-8 text-xs"
              />
            </div>
          )}

          <div className="max-h-60 overflow-y-auto">
            {visiveis.length === 0 ? (
              <p className="px-3 py-4 text-center text-xs text-conteudo-fraco">
                Nenhuma opção encontrada.
              </p>
            ) : (
              visiveis.map((opcao) => (
                <div
                  key={opcao.valor}
                  className="rounded px-2 py-1.5 hover:bg-superficie-2"
                  role="option"
                  aria-selected={selecionados.includes(opcao.valor)}
                >
                  <Caixa
                    checked={selecionados.includes(opcao.valor)}
                    onChange={() => alternar(opcao.valor)}
                    rotulo={opcao.rotulo}
                    dica={opcao.descricao}
                  />
                </div>
              ))
            )}
          </div>

          {selecionados.length > 0 && (
            <div className="mt-1 border-t border-borda pt-1">
              <button
                type="button"
                onClick={() => onAlterar([])}
                className="w-full rounded px-2 py-1.5 text-left text-xs font-medium text-conteudo-suave hover:bg-superficie-2"
              >
                Limpar seleção
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
